import { transition, useBreakpointValue, useTheme } from '@hover/blueprint';
import type { Breakpoints } from '@hover/blueprint/foundation';
import type { TargetAndTransition, Variant } from 'framer-motion';
import { useMemo } from 'react';

import { presenceVariants } from 'src/shared/helpers/motion';

import { useIsFullscreenImageOpen, useStickyDepth } from '../../hooks';
import type {
  AiOrSampleDisplayImage,
  AiOrSampleGeneratedImage,
} from '../../types';
import { isAiDisplay, isAiGenerated, isDisplay } from '../../types';

import type { Direction } from './types';

export const useFullscreenTransition = () => {
  const {
    motion: { framer: spring },
  } = useTheme();

  return useMemo(() => ({ ...spring.firm, damping: 17 }), [spring.firm]);
};

export const useCarouselTransition = () =>
  useMemo(
    () => ({
      type: 'spring',
      bounce: 0,
      damping: 50,
      stiffness: 300,
    }),
    [],
  );

export const useFullscreenDepth = () => useStickyDepth() + 1;

/**
 * Get unique identifier used for the zoom animation from fullscreen thumbnail
 * to fullscreen image
 *
 * @param image original or generated image
 */
export const getFullscreenImageLayoutId = (
  image: AiOrSampleDisplayImage | AiOrSampleGeneratedImage,
) => {
  if (isDisplay(image)) {
    return `fullscreen-image-${isAiDisplay(image) ? image.id : image.sampleId}`;
  }

  return `fullscreen-generated-image-${
    isAiGenerated(image) ? image.id : image.sampleId
  }`;
};

/**
 * The breakpoint at which we hide the style description and original image
 * toggle and display them as sheets instead
 */
export const layoutBreakpoint = 'width250' satisfies keyof Breakpoints;

export const useFullscreenImageBreakpointValue = <T extends {}>(
  mobile: T,
  desktop: T,
) =>
  useBreakpointValue(
    { base: mobile, [layoutBreakpoint]: desktop },
    { fallback: layoutBreakpoint },
  );

/**
 * Media query hook for determining whether we're in the mobile fullscreen image
 * layout which breaks at `layoutBreakpoint` instead of `tablet`
 */
export const useIsFullscreenImageMobile = () =>
  useFullscreenImageBreakpointValue(true, false);

/**
 * Get styles to be applied to an element that should be hidden when entering
 * and exiting the fullscreen image view. This is necessary for anything that
 * potentially overlaps with the fullscreen image layout transition.
 *
 * @returns style object to be applied via `sx`
 */
export const useHideInFullscreen = () => {
  const isFullscreenImageOpen = useIsFullscreenImageOpen();

  return {
    opacity: isFullscreenImageOpen ? '0 !important' : '1 !important',
    ...transition.easing250('opacity'),
    transitionDelay: '0.2s',
  };
};

export interface PresenceVariantsWithDirectionOptions {
  left: TargetAndTransition;
  right: TargetAndTransition;
  center: Variant;
}

export const presenceVariantsWithDirection = (
  direction: Direction,
  { left, right, center }: PresenceVariantsWithDirectionOptions,
  child: boolean = false,
) =>
  presenceVariants(
    {
      animate: center,
      initial: direction === 'previous' ? left : right,
      exit: (d: Direction) => (d === 'previous' ? right : left),
    },
    child,
  );
